import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { toast } from 'react-hot-toast';
import Modal from '../Modal';
import {
  LeadSourceDto,
  CreateLeadSourceDto,
  UpdateLeadSourceDto,
  LeadSourceType,
} from '../../services/settingsService';
import { handleApiError } from '../../utils/errorHandler';

const ModalContent = styled.div`
  padding: 24px;
  width: 100%;
  max-width: 500px;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--border-color);
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: var(--text-secondary);
  padding: 4px;

  &:hover {
    color: var(--text-primary);
  }
`;

const FormSection = styled.div`
  margin-bottom: 24px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);

  &.required::after {
    content: " *";
    color: var(--error-color);
  }
`;

const Input = styled.input<{ hasError?: boolean }>`
  padding: 12px;
  border: 1px solid ${props => props.hasError ? 'var(--error-color)' : 'var(--border-color)'};
  border-radius: var(--border-radius-md);
  font-size: 14px;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--primary-light);
  }

  &::placeholder {
    color: var(--text-secondary);
  }
`;

const Select = styled.select<{ hasError?: boolean }>`
  padding: 12px;
  border: 1px solid ${props => props.hasError ? 'var(--error-color)' : 'var(--border-color)'};
  border-radius: var(--border-radius-md);
  font-size: 14px;
  background: var(--surface-color);
  color: var(--text-primary);
  cursor: pointer;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--primary-light);
  }
`;

const TextArea = styled.textarea<{ hasError?: boolean }>`
  padding: 12px;
  border: 1px solid ${props => props.hasError ? 'var(--error-color)' : 'var(--border-color)'};
  border-radius: var(--border-radius-md);
  font-size: 14px;
  resize: vertical;
  min-height: 80px;
  font-family: inherit;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--primary-light);
  }

  &::placeholder {
    color: var(--text-secondary);
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;

  input[type="checkbox"] {
    margin: 0;
  }
`;

const ErrorMessage = styled.div`
  color: var(--error-color);
  font-size: 12px;
  margin-top: 4px;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const InfoNote = styled.div`
  background: var(--background-light);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  padding: 10px 12px;
  font-size: 12px;
  color: var(--text-secondary);
  line-height: 1.5;
  margin-bottom: 16px;
`;

const SubHeading = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 8px 0 12px;
  padding-top: 16px;
  border-top: 1px solid var(--border-color);
`;

const LinkedBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--text-secondary);
  background: var(--background-light);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  padding: 10px 12px;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid var(--border-color);
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 12px 24px;
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => props.variant === 'primary' ? `
    background: var(--primary-color);
    color: white;

    &:hover {
      background: var(--primary-light); color: var(--primary-color);
    }

    &:disabled {
      background: var(--border-color);
      cursor: not-allowed;
    }
  ` : `
    background: var(--surface-color);
    color: var(--text-primary);
    border: 1px solid var(--border-color);

    &:hover {
      background: var(--hover-background);
    }
  `}
`;

interface LeadSourceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (leadSource: CreateLeadSourceDto | UpdateLeadSourceDto) => Promise<void>;
  leadSource?: LeadSourceDto | null;
  title: string;
}

const emptyForm = {
  name: '',
  description: '',
  isActive: true,
  type: LeadSourceType.Organization as LeadSourceType,
  signingAmount: '',
  approvalPercentage: '',
  createLogin: false,
  loginEmail: '',
  loginPassword: '',
};

const LeadSourceModal: React.FC<LeadSourceModalProps> = ({
  isOpen,
  onClose,
  onSave,
  leadSource,
  title
}) => {
  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  // רשומה שכבר מקושרת ל-login: לא ניתן להוסיף התחברות נוספת; מוצג הדוא"ל לקריאה-בלבד.
  const isLinked = !!leadSource?.linkedUserId;

  useEffect(() => {
    if (isOpen) {
      if (leadSource) {
        setFormData({
          name: leadSource.name,
          description: leadSource.description || '',
          isActive: leadSource.isActive,
          type: leadSource.type,
          signingAmount:
            leadSource.defaultSigningCommissionAmount != null
              ? String(leadSource.defaultSigningCommissionAmount)
              : '',
          approvalPercentage:
            leadSource.defaultApprovalCommissionPercentage != null
              ? String(leadSource.defaultApprovalCommissionPercentage)
              : '',
          createLogin: false,
          loginEmail: '',
          loginPassword: '',
        });
      } else {
        setFormData(emptyForm);
      }
      setErrors({});
    }
  }, [isOpen, leadSource]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'שם הרשומה הוא שדה חובה';
    }

    // עמלות אופציונליות — אך אם הוזנו, חייבות להיות מספר תקין ולא שלילי.
    const signing = formData.signingAmount.trim();
    if (signing !== '' && (isNaN(Number(signing)) || Number(signing) < 0)) {
      newErrors.signingAmount = 'סכום עמלה לא תקין';
    }
    const approval = formData.approvalPercentage.trim();
    if (approval !== '' && (isNaN(Number(approval)) || Number(approval) < 0 || Number(approval) > 100)) {
      newErrors.approvalPercentage = 'אחוז עמלה חייב להיות בין 0 ל-100';
    }

    // provisioning של login — רק כאשר מבקשים ליצור התחברות ואין כבר קישור.
    if (!isLinked && formData.createLogin) {
      if (!formData.loginEmail.trim()) {
        newErrors.loginEmail = 'יש להזין דוא"ל להתחברות';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.loginEmail.trim())) {
        newErrors.loginEmail = 'כתובת דוא"ל לא תקינה';
      }
      if (!formData.loginPassword) {
        newErrors.loginPassword = 'יש להזין סיסמה';
      } else if (formData.loginPassword.length < 6) {
        newErrors.loginPassword = 'הסיסמה חייבת לכלול לפחות 6 תווים';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const toNum = (s: string): number | null => (s.trim() === '' ? null : Number(s));
      const wantsLogin = !isLinked && formData.createLogin;

      const leadSourceData: CreateLeadSourceDto | UpdateLeadSourceDto = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        isActive: formData.isActive,
        type: formData.type,
        defaultSigningCommissionAmount: toNum(formData.signingAmount),
        defaultApprovalCommissionPercentage: toNum(formData.approvalPercentage),
        createLogin: wantsLogin,
        loginEmail: wantsLogin ? formData.loginEmail.trim() : undefined,
        loginPassword: wantsLogin ? formData.loginPassword : undefined,
      };

      await onSave(leadSourceData);
      onClose();
    } catch (error) {
      toast.error(handleApiError(error, 'שגיאה בשמירת הרשומה'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>{title}</ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>

        <FormSection>
          <FormGroup>
            <Label className="required">שם</Label>
            <Input
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="שם הסוכן / הארגון"
              hasError={!!errors.name}
            />
            {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label className="required">סוג</Label>
            <Select
              value={formData.type}
              onChange={(e) => handleInputChange('type', Number(e.target.value) as LeadSourceType)}
            >
              <option value={LeadSourceType.Agent}>סוכן</option>
              <option value={LeadSourceType.Organization}>ארגון</option>
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>תיאור</Label>
            <TextArea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="הזן תיאור (אופציונלי)"
            />
          </FormGroup>

          {/* עמלות ברירת-מחדל — גלויות לכל הצוות; מסך זה ממילא למנהל בלבד. */}
          <SubHeading>עמלות ברירת מחדל</SubHeading>
          <InfoNote>
            אלו ערכי <strong>ברירת מחדל</strong>. בעת שיוך הסוכן/הארגון לתיק הם יועתקו לתיק וניתן יהיה לערוך
            אותם שם — שינוי כאן לא ישפיע על תיקים קיימים. כל עמלה אופציונלית (אפשר להשאיר ריק).
          </InfoNote>
          <Row>
            <FormGroup>
              <Label>עמלת חתימה (₪)</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={formData.signingAmount}
                onChange={(e) => handleInputChange('signingAmount', e.target.value)}
                placeholder="סכום קבוע"
                hasError={!!errors.signingAmount}
              />
              {errors.signingAmount && <ErrorMessage>{errors.signingAmount}</ErrorMessage>}
            </FormGroup>
            <FormGroup>
              <Label>עמלת אישור (%)</Label>
              <Input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={formData.approvalPercentage}
                onChange={(e) => handleInputChange('approvalPercentage', e.target.value)}
                placeholder="אחוז"
                hasError={!!errors.approvalPercentage}
              />
              {errors.approvalPercentage && <ErrorMessage>{errors.approvalPercentage}</ErrorMessage>}
            </FormGroup>
          </Row>

          {/* התחברות (login) — Role=Agent. אופציונלי, וגם לארגון. */}
          <SubHeading>התחברות למערכת</SubHeading>
          {isLinked ? (
            <LinkedBadge>
              משתמש מקושר: <strong>{leadSource?.linkedUserEmail}</strong>
              {leadSource?.linkedUserActive === false && ' (מושבת)'}
            </LinkedBadge>
          ) : (
            <>
              <CheckboxGroup>
                <CheckboxLabel>
                  <input
                    type="checkbox"
                    checked={formData.createLogin}
                    onChange={(e) => handleInputChange('createLogin', e.target.checked)}
                  />
                  צור התחברות (חשבון סוכן)
                </CheckboxLabel>
              </CheckboxGroup>

              {formData.createLogin && (
                <>
                  <FormGroup>
                    <Label className="required">דוא"ל להתחברות</Label>
                    <Input
                      type="email"
                      value={formData.loginEmail}
                      onChange={(e) => handleInputChange('loginEmail', e.target.value)}
                      placeholder="name@example.com"
                      hasError={!!errors.loginEmail}
                    />
                    {errors.loginEmail && <ErrorMessage>{errors.loginEmail}</ErrorMessage>}
                  </FormGroup>
                  <FormGroup>
                    <Label className="required">סיסמה</Label>
                    <Input
                      type="password"
                      value={formData.loginPassword}
                      onChange={(e) => handleInputChange('loginPassword', e.target.value)}
                      placeholder="לפחות 6 תווים"
                      hasError={!!errors.loginPassword}
                    />
                    {errors.loginPassword && <ErrorMessage>{errors.loginPassword}</ErrorMessage>}
                  </FormGroup>
                </>
              )}
            </>
          )}

          <CheckboxGroup>
            <CheckboxLabel>
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => handleInputChange('isActive', e.target.checked)}
              />
              פעיל
            </CheckboxLabel>
          </CheckboxGroup>

        </FormSection>

        <ModalFooter>
          <Button variant="secondary" onClick={onClose}>
            ביטול
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={loading}>
            {loading ? 'שומר...' : 'שמירה'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default LeadSourceModal;
